import React from 'react';
import { InView } from 'react-intersection-observer';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Parallax } from 'react-scroll-parallax';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  visuallyHidden,
} from '../styles';
import { Button, Container, RichText } from './ui';
import TrustpilotWidget from './TrustpilotWidget';
import BeeBadge from './BeeBadge';
import logo from '../images/logo.svg';

const StyledBanner = styled.section``;

const StyledOuter = styled.div`
  padding: 10px 0 20px;

  ${minBreakpointQuery.tiny`
    padding-bottom: 30px;
  `}

  ${minBreakpointQuery.small`
    padding-bottom: 40px;
  `}

  ${minBreakpointQuery.medium`
    padding-bottom: 60px;
  `}

  ${minBreakpointQuery.large`
    padding-bottom: 80px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-bottom: 100px;
  `}
`;

const StyledInner = styled.div`
  ${minBreakpointQuery.smedium`
    display: grid;
    grid-template-columns: 2.2fr 1fr;
    grid-template-rows: repeat(3, auto);
    align-items: center;
    column-gap: 10px;
  `}

  ${minBreakpointQuery.xxlarge`
    grid-template-rows: repeat(4, auto);
  `}
`;

const StyledTop = styled.div`
  ${minBreakpointQuery.xxlarge`
    display: grid;
    grid-template-columns: 580px 1fr;
  `}
`;

const StyledTrustpilotTextLogoWrapper = styled.div`
  display: grid;
  gap: 20px;

  ${maxBreakpointQuery.smedium`
    width: 80%;
  `}
`;

const StyledTrustpilotWidget = styled(TrustpilotWidget)`
  justify-self: start;
`;

const StyledText = styled.div`
  max-width: 360px;
  font-weight: ${fontWeights.medium};

  p {
    ${fluidFontSize(
      '18px',
      '28px',
      breakpointSizes.tiny,
      breakpointSizes.xxxxlarge,
    )};
  }
`;

const StyledLogo = styled.img``;

const StyledBadge = styled(BeeBadge)`
  ${maxBreakpointQuery.xxlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.xxxlarge`
    margin-top: 35px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-top: 40px;
  `}
`;

const StyledHeading = styled.h1`
  margin-top: 12px;
  color: ${brandColours.primary};
  font-weight: ${fontWeights.regular};
  ${fluidFontSize(
    '56px',
    '122px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 0.9;

  ${minBreakpointQuery.small`
    margin-top: 16px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 20px;
  `}
`;

const StyledImages = styled.div`
  display: grid;
  margin: auto;

  ${maxBreakpointQuery.smedium`
    max-width: 500px;
  `}

  ${minBreakpointQuery.smedium`
    grid-column: 2/3;
    grid-row: 1/4;
  `}

  ${minBreakpointQuery.xxlarge`
    grid-row: 1/5;
    margin-right: -30px;
    margin-bottom: -260px;
    margin-left: -30px;
  `}

  ${minBreakpointQuery.xxxlarge`
    margin-right: -60px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-right: -120px;
  `}
`;

const StyledImageWrapper = styled.div`
  grid-area: 1 / 1 / 1 / 1;

  &:first-child {
    margin-top: 40px;
    text-align: right;

    ${minBreakpointQuery.smedium`
      margin-top: 60px;
    `}

    ${minBreakpointQuery.large`
      margin-top: 90px;
    `}

    ${minBreakpointQuery.xlarge`
      margin-top: 120px;
    `}

    ${minBreakpointQuery.xxlarge`
      margin-top: 160px;
    `}

    ${minBreakpointQuery.xxxlarge`
      margin-top: 200px;
    `}

    ${minBreakpointQuery.xxxxlarge`
      margin-top: 240px;
    `}
  }
`;

const StyledImage = styled(GatsbyImage)`
  width: 100%;
  max-width: 260px;

  ${minBreakpointQuery.tiny`
    max-width: 280px;
  `}

  ${minBreakpointQuery.tsmall`
    max-width: 300px;
  `}

  ${minBreakpointQuery.small`
    max-width: 320px;
  `}

  ${minBreakpointQuery.smedium`
    max-width: 470px;
  `}
`;

const StyledUsps = styled.ul`
  display: flex;
  margin-top: 20px;
  overflow: auto;

  ${maxBreakpointQuery.xxlarge`
    margin-right: -30px;
  `}

  ${maxBreakpointQuery.smedium`
    margin-right: -20px;
  `}

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.smedium`
    grid-column: 1/3;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.xxlarge`
    grid-column: 1/2;
  `}

  &:before {
    content: '+';
    margin-right: 20px;
    font-weight: ${fontWeights.bold};
    ${fontSize(68)};

    ${minBreakpointQuery.small`
      margin-right: 25px;
    `}

    ${minBreakpointQuery.large`
      margin-right: 30px;
    `}
  }
`;

const StyledUsp = styled.li`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  min-width: 140px;
  font-weight: ${fontWeights.semibold};
  ${fontSize(14)};
  border-left: 1px solid ${brandColours.quaternary};

  ${minBreakpointQuery.small`
    padding-right: 15px;
    padding-left: 15px;
    min-width: 155px;
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.large`
    padding-right: 16px;
    padding-left: 16px;
    min-width: 170px;
    ${fontSize(16)};
  `}
`;

const StyledButtons = styled.div`
  ${minBreakpointQuery.smedium`
    display: flex;
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
  border-radius: 0;

  ${minBreakpointQuery.smedium`
    display: flex;
    justify-content: center;
    align-items: center;
  `}

  ${minBreakpointQuery.large`
    max-width: 400px;
  `}
`;

const Banner = ({
  text,
  heading,
  images,
  usps,
  links,
  setBannerLogoInView,
}) => (
  <StyledBanner>
    <StyledOuter>
      <Container>
        <StyledInner>
          <StyledTop>
            <StyledTrustpilotTextLogoWrapper>
              <StyledTrustpilotWidget />
              <StyledText>
                <RichText content={text} />
              </StyledText>
              <InView onChange={inView => setBannerLogoInView(inView)}>
                <StyledLogo src={logo} alt="Ecotalk logo" />
              </InView>
            </StyledTrustpilotTextLogoWrapper>
            <StyledBadge />
          </StyledTop>
          <StyledHeading>{heading}</StyledHeading>
          <StyledImages>
            {images.map(({ gatsbyImageData, title }, i) => (
              <StyledImageWrapper key={i}>
                <Parallax speed={i === 0 ? 5 : -5}>
                  <StyledImage
                    image={gatsbyImageData}
                    alt={title}
                    loading="eager"
                    fetchpriority="high"
                  />
                </Parallax>
              </StyledImageWrapper>
            ))}
          </StyledImages>
          <StyledUsps>
            {usps.map((usp, id) => (
              <StyledUsp key={id}>{usp}</StyledUsp>
            ))}
          </StyledUsps>
        </StyledInner>
      </Container>
    </StyledOuter>
    <StyledButtons>
      {links.map(({ text, pageUrl }, i) => (
        <StyledButton key={i} to={pageUrl} isAlt={i === 0} isLarge={true}>
          {text}
        </StyledButton>
      ))}
    </StyledButtons>
  </StyledBanner>
);

export default Banner;
